/* AUTO-SYNCED from PsyKit.io/hooks/use-bilateral-broadcast.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { getSocket } from "@/lib/socket-client";
import type { CheckinActive, CheckinLiveData } from "@/lib/checkin-types";

export interface BroadcastPayload {
  xNorm: number;
  /** Tempo in true Hz (0.5–2.5). 1 cycle = left → right → left. */
  hz: number;
  /** Seconds elapsed since session start on the therapist's side. */
  elapsedSeconds: number;
  ballSize: number;
  ballColor: string;
  canvasBg: string;
  isRunning: boolean;
  /** Whether audio playback is active on the therapist side. */
  audioOn: boolean;
  /** Master volume 0–1. */
  audioVolume: number;
  /**
   * Active audio preset ID:
   * "theta"|"alpha"|"delta"|"off" = legacy built-ins,
   * SOUND_LIBRARY id (e.g. "pink_noise") = library sound,
   * "custom:<uuid>" = therapist-only upload (client skips audio).
   */
  audioPreset: string;
  /** Whether the therapist has enabled controller-haptics for this session.
   *  Free-tier sessions always send false regardless of local toggle state —
   *  the gate lives on the therapist side, the client view just mirrors. */
  hapticsOn: boolean;
  /** Pulse intensity 0..1 — applied on whichever motor matches the edge. */
  hapticsIntensity: number;
}

/**
 * Publishes bilateral session state to a Socket.IO room keyed by sessionCode.
 * Used by the therapist session page. Each session code maps to a unique room
 * so multiple therapists never bleed into each other.
 */
export function useBilateralBroadcast(sessionCode: string) {
  const frameCountRef = useRef(0);
  const [clientCheckinPhaseComplete, setClientCheckinPhaseComplete] = useState<"pre" | "post" | null>(null);
  // Latest live check-in answers as edited by the CLIENT (mirrored back to us).
  const [clientCheckinData, setClientCheckinData] = useState<CheckinLiveData | null>(null);

  // Cache the last emitted frame payload so we can replay it on sync_request
  const lastPayloadRef = useRef<BroadcastPayload | null>(null);
  // Track whether one-time session events have already been broadcast
  const sessionStartedRef = useRef(false);
  const sessionEndedRef = useRef(false);
  const sessionSavedRef = useRef(false);
  // Latest check-in state/answers, for replay when a (re)joining client syncs.
  // lastCheckinDataRef holds the freshest answers regardless of which side
  // last edited them, so replay is always authoritative.
  const lastCheckinStateRef = useRef<CheckinActive>(null);
  const lastCheckinDataRef = useRef<CheckinLiveData | null>(null);

  useEffect(() => {
    if (!sessionCode) return;

    const socket = getSocket();
    socket.emit("join", { sessionCode });

    const handleCheckinComplete = ({ phase }: { phase: "pre" | "post" }) => {
      setClientCheckinPhaseComplete(phase ?? "pre");
    };
    socket.on("checkin_complete", handleCheckinComplete);

    // Client edited the shared check-in form — mirror their answers to us.
    const handleCheckinData = (payload: CheckinLiveData) => {
      lastCheckinDataRef.current = payload;
      setClientCheckinData(payload);
    };
    socket.on("checkin_data", handleCheckinData);

    // When a new client joins the room, the server emits sync_request to us.
    // Immediately re-emit the last known state so the client doesn't have to wait
    // for the next animation frame tick.
    const handleSyncRequest = () => {
      const sock = getSocket();
      if (lastPayloadRef.current) sock.emit("frame", lastPayloadRef.current);
      if (sessionStartedRef.current) sock.emit("session_start", {});
      if (sessionEndedRef.current) sock.emit("session_end", {});
      if (sessionSavedRef.current) sock.emit("session_saved", {});
      // Restore the open check-in form + its current answers on the client.
      if (lastCheckinStateRef.current) sock.emit("checkin_state", { active: lastCheckinStateRef.current });
      if (lastCheckinDataRef.current) sock.emit("checkin_data", lastCheckinDataRef.current);
    };
    socket.on("sync_request", handleSyncRequest);

    return () => {
      socket.off("checkin_complete", handleCheckinComplete);
      socket.off("checkin_data", handleCheckinData);
      socket.off("sync_request", handleSyncRequest);
      socket.emit("leave", { sessionCode });
      frameCountRef.current = 0;
    };
  }, [sessionCode]);

  /**
   * Throttled to ~20fps (every 3rd call). Carries Hz and elapsed seconds so
   * the client can run the same sinusoidal formula and extrapolate between frames.
   */
  const broadcastFrame = useCallback(
    (
      xNorm: number,
      hz: number,
      elapsedSeconds: number,
      ballSize: number,
      ballColor: string,
      canvasBg: string,
      isRunning: boolean,
      audioOn: boolean,
      audioVolume: number,
      audioPreset: string,
      hapticsOn: boolean,
      hapticsIntensity: number
    ) => {
      frameCountRef.current++;
      if (frameCountRef.current % 3 !== 0) return;
      const payload: BroadcastPayload = {
        xNorm, hz, elapsedSeconds, ballSize, ballColor, canvasBg,
        isRunning, audioOn, audioVolume, audioPreset,
        hapticsOn, hapticsIntensity,
      };
      lastPayloadRef.current = payload;
      getSocket().emit("frame", payload);
    },
    []
  );

  /**
   * Immediate (not throttled). Sent on pause, stop, and visual prop changes.
   * Ensures client sees the correct state even when the ball isn't moving.
   */
  const broadcastSync = useCallback(
    (
      xNorm: number,
      hz: number,
      elapsedSeconds: number,
      ballSize: number,
      ballColor: string,
      canvasBg: string,
      isRunning: boolean,
      audioOn: boolean,
      audioVolume: number,
      audioPreset: string,
      hapticsOn: boolean,
      hapticsIntensity: number
    ) => {
      const payload: BroadcastPayload = {
        xNorm, hz, elapsedSeconds, ballSize, ballColor, canvasBg,
        isRunning, audioOn, audioVolume, audioPreset,
        hapticsOn, hapticsIntensity,
      };
      lastPayloadRef.current = payload;
      getSocket().emit("frame", payload);
    },
    []
  );

  /**
   * Broadcasts a one-time session-end signal to the client view.
   * Call this when the therapist ends the session so the client can show
   * the post-session check-in.
   */
  const broadcastSessionEnd = useCallback(() => {
    sessionEndedRef.current = true;
    getSocket().emit("session_end", {});
  }, []);

  /**
   * Broadcasts a session_start signal so the client can close their pre-checkin
   * and show the mirror canvas.
   */
  const broadcastSessionStart = useCallback(() => {
    sessionStartedRef.current = true;
    getSocket().emit("session_start", {});
  }, []);

  /**
   * Broadcasts a session_saved signal so the client's post-checkin overlay
   * closes automatically once the therapist has saved the session.
   */
  const broadcastSessionSaved = useCallback(() => {
    sessionSavedRef.current = true;
    getSocket().emit("session_saved", {});
  }, []);

  /**
   * Tell the client which check-in form to show (or `null` to dismiss it).
   * This is the ONLY thing that opens a check-in on the client — there is no
   * automatic/timer-based showing.
   */
  const broadcastCheckinState = useCallback((active: CheckinActive) => {
    lastCheckinStateRef.current = active;
    getSocket().emit("checkin_state", { active });
  }, []);

  /**
   * Push the therapist's current check-in answers to the client (live sync).
   * Call this only from local edits — applying an inbound `checkin_data` must
   * NOT re-broadcast, or the two sides ping-pong forever.
   */
  const broadcastCheckinData = useCallback((data: CheckinLiveData) => {
    lastCheckinDataRef.current = data;
    getSocket().emit("checkin_data", data);
  }, []);

  return {
    broadcastFrame,
    broadcastSync,
    broadcastSessionEnd,
    broadcastSessionStart,
    broadcastSessionSaved,
    broadcastCheckinState,
    broadcastCheckinData,
    clientCheckinPhaseComplete,
    clientCheckinData,
  };
}
